import { useSuspenseQuery } from '~/services'
import { useActiveSubscription } from './useActiveSubscription'
import { COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID } from '../constants'
import Stripe from 'stripe'
import { AddonDetails, AddonQuotaType, ProductWithPrices } from '../types/index'
import { match } from 'ts-pattern'
import { formatters } from '~/utils'

export const ADDONS_QUOTA_TYPES = [
  'invocation_calls',
  'table_row_count',
  'bot_count',
  'always_alive',
  'workspace_member_count',
  'knowledgebase_vector_storage',
  'storage_count',
] as const

export const useAddons = (workspaceId: string) => {
  const { data: products } = useSuspenseQuery('workspaces_/$workspaceId_/billing/products', { workspaceId })
  const { data: subscriptionSchedule } = useSuspenseQuery('workspaces_/$workspaceId_/billing/subscriptionSchedule', {
    workspaceId,
  })
  const { monthly: monthlyActiveSubscription } = useActiveSubscription(workspaceId)

  const addonProducts = products.filter(
    (product) => ![COMMUNITY_PLAN_PRODUCT_ID, PLUS_PLAN_PRODUCT_ID, TEAM_PLAN_PRODUCT_ID].includes(product.id)
  )

  const addons = getAddonDetails(addonProducts, monthlyActiveSubscription?.items.data ?? [])

  updateNextPaymentAddonQuantity(addons, subscriptionSchedule)

  return {
    addons,
  }
}

const getAddonDetails = (
  products: ProductWithPrices[],
  subscriptionItems: Stripe.SubscriptionItem[]
): AddonDetails[] => {
  const addonDetails = products.map((product) => {
    const quantity = subscriptionItems.find((item) => item.price.product === product.id)?.quantity ?? 0

    const description = match(product.metadata.quotaName as AddonQuotaType)
      .with('bot_count', () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} per bot`)
      .with('table_row_count', () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} per 100,000 rows`)
      .with(
        'knowledgebase_vector_storage',
        () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} per 1GB of storage`
      )
      .with('storage_count', () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} per 10GB of storage`)
      .with('always_alive', () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} per bot`)
      .with('workspace_member_count', () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} per seat`)
      .with(
        'invocation_calls',
        () => `${formatters.$centdollars(product.price.unit_amount ?? 0, 0)} for 5,000 messages per month`
      )
      .exhaustive()

    return {
      productId: product.id,
      currentQuantity: quantity,
      nextPaymentQuantity: quantity,
      priceId: product.price.id,
      productDetails: product,
      name: product.name,
      displayName: product.name.split(' - ')[1]!,
      quotaName: product.metadata.quotaName as AddonQuotaType,
      priceCents: product.price.unit_amount ?? 0,
      description,
    }
  })

  return addonDetails
}

const updateNextPaymentAddonQuantity = (addons: AddonDetails[], subscriptionSchedule: Stripe.SubscriptionSchedule) => {
  if (subscriptionSchedule.phases?.[1]) {
    addons.forEach((addon) => (addon.nextPaymentQuantity = 0))

    subscriptionSchedule.phases[1].items.map((item) => {
      const addon = addons.find((addon) => addon.priceId === item.price.toString())

      if (addon) {
        addon.nextPaymentQuantity = item.quantity ?? 0
      }
    })
  }
}
